import React, { useState, useEffect } from "react"
import moment from "moment"

import { getGenderValue, getMealValue, getSeatValue } from "../../../lib/utils"
import styles from "./index.module.css"
import AdminTable from '../../../components/AdminTable'
import { useData } from "../../../contexts/DataContext"
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll"
import { fetchBookings, fetchAirports } from "../../../lib/queries"
import { syncBookingToGoogleSheet } from "../../../lib/actions"

function SyncBookingButton({ bookingId, syncBookingToGoogleSheet }) {
    const [syncStatus, setSyncStatus] = useState('idle')
    const [syncError, setSyncError] = useState('')

    const handleSync = async () => {
        setSyncStatus('loading')
        setSyncError('')
        const result = await syncBookingToGoogleSheet(bookingId)
        if (result.success) {
            setSyncStatus('success')
            // setTimeout(() => setSyncStatus('idle'), 2000)
        } else {
            setSyncStatus('error')
            setSyncError(result.error || 'Unknown error')
        }
    }

    return (
        <span onClick={syncStatus === 'idle' ? handleSync : undefined} style={{ cursor: syncStatus === 'idle' ? 'pointer' : 'default' }}>
            {syncStatus === 'idle' && <span className="material-symbols-outlined">backup</span>}
            {syncStatus === 'loading' && <span className="spinner-border spinner-border-sm" />}
            {syncStatus === 'success' && <span className="material-symbols-outlined text-success">check_circle</span>}
            {syncStatus === 'error' && (
                <span className="material-symbols-outlined text-danger" title={syncError}>error</span>
            )}
        </span>
    )
}

function OrdersTable() {
    const [searchTerm, setSearchTerm] = useState("")
    const [expandedRow, setExpandedRow] = useState(false)
    const [airports, setAirports] = useState([])
    const { inventory } = useData()

    const {
        items: orders,
        ref,
        isLoading,
        isError,
        error,
        isFetchingNextPage,
        hasNextPage
    } = useInfiniteScroll('bookings', fetchBookings)

    // Load airports from Firebase
    useEffect(() => {
        const loadAirports = async () => {
            try {
                const airportsData = await fetchAirports()
                setAirports(airportsData)
            } catch (error) {
                console.error("Error loading airports:", error)
            }
        }
        loadAirports()
    }, [])

    // Helper function to get airport name by code
    const getAirportName = (code) => {
        if (!code || !airports.length) return code || "Unknown"
        const airport = airports.find(airport => airport.code === code)
        return airport ? airport.label : code
    }

    const filteredOrders = orders.filter(order =>
        order.passengers.some(passenger =>
            passenger.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            passenger.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            passenger.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            passenger.phone.includes(searchTerm)
        )
    )

    const toggleExpandedRow = (id) => {
        if (expandedRow === id) {
            setExpandedRow(false)
        } else {
            setExpandedRow(id)
        }
    }

    if (isLoading) return <div>Loading...</div>
    if (isError) return <div>Error: {error.message}</div>

    return (
        <section>
            <div className="row">
                <div className="col-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Name, Email, Phone"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            <AdminTable>
                <thead className="thead-light">
                    <tr>
                        <th>#</th>
                        <th>Booking ID</th>
                        <th>Date Added</th>
                        <th>Departure</th>
                        <th>Arrival</th>
                        <th>Departure Date</th>
                        <th>Return Date</th>
                        <th>Promo</th>
                        <th>Booking Status</th>
                        <th>Passengers</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredOrders.map((order, index) =>
                        <React.Fragment key={`${order.id}-${index}`}>

                            <tr key={`${order.id}-${index}`} className={moment(inventory.find((item) => item.id === order.outboundFlight)?.departureDate).isBefore(moment().startOf('day')) ? "table-secondary" : "abc"}>
                                <td>{index + 1}</td>
                                <td className="text-uppercase">{order.id.substring(order.id.length - 5)}</td>
                                <td>{moment(order.dateAdded?.toDate()).format("lll")}</td>
                                <td>{getAirportName(inventory.find((item) => item.id === order.outboundFlight)?.departure)}</td>
                                <td>{getAirportName(inventory.find((item) => item.id === order.outboundFlight)?.arrival)}</td>
                                <td>{inventory.find((item) => item.id === order.outboundFlight)?.departureDate}</td>
                                <td>{order.inboundFlight ? inventory.find((item) => item.id === order.inboundFlight)?.departureDate : "One Way"}</td>
                                <td><span className="badge bg-primary">{order.promoCode?.toUpperCase() || "NO PROMO"}</span></td>
                                <td><span className={`badge ${order.status === "paid" ? "bg-success" : "bg-danger"}`}>{order.status?.toUpperCase() || "NO STATUS"}</span></td>
                                <td>{order.passengers.length}</td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <span
                                            className={`material-symbols-outlined ${styles.icon} ${expandedRow === `PassengerDetails-${order.id}` ? styles.activeIcon : ""}`}
                                            onClick={() => toggleExpandedRow(`PassengerDetails-${order.id}`)}
                                            title="View Passenger Details"
                                        >
                                            person
                                        </span>
                                        <span
                                            className={`material-symbols-outlined ${styles.icon} ${expandedRow === `BillingDetails-${order.id}` ? styles.activeIcon : ""}`}
                                            onClick={() => toggleExpandedRow(`BillingDetails-${order.id}`)}
                                            title="View Billing Details"
                                        >
                                            receipt_long
                                        </span>
                                        <SyncBookingButton bookingId={order.id} syncBookingToGoogleSheet={syncBookingToGoogleSheet} />
                                    </div>
                                </td>
                            </tr>

                            {/* Passenger Details */}
                            {expandedRow === `PassengerDetails-${order.id}` && (
                                <>
                                    {order.passengers?.length > 0 ?
                                        <tr>
                                            <td colSpan="10">
                                                <table className="table table-sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Email</th>
                                                            <th>Phone</th>
                                                            <th>DOB</th>
                                                            <th>Gender</th>
                                                            <th>Nationality</th>
                                                            <th>Passport</th>
                                                            <th>Passport Expiration</th>
                                                            <th>Seat Type</th>
                                                            <th>Meal</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {order.passengers.map((passenger, pIndex) => (
                                                            <tr key={pIndex}>
                                                                <td>{passenger?.firstName} {passenger?.lastName}</td>
                                                                <td>{passenger?.email}</td>
                                                                <td>{passenger?.phone}</td>
                                                                <td>{moment(passenger?.dob?.toDate?.()).format("ll")}</td>
                                                                <td>{getGenderValue(passenger?.gender)}</td>
                                                                <td>{passenger?.nationality}</td>
                                                                <td>{passenger?.passportNumber} ({passenger?.passportCountry})</td>
                                                                <td>{moment(passenger?.passportExpiration?.toDate?.()).format("ll")}</td>
                                                                <td>{getSeatValue(passenger?.seatType)}</td>
                                                                <td>{getMealValue(passenger?.meal) || "--"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr> :
                                        <tr>
                                            <td>No passenger found</td>
                                        </tr>}
                                </>
                            )}

                            {/* Billing Details */}
                            {expandedRow === `BillingDetails-${order.id}` && (
                                <>{order.billingDetails ?
                                    <tr>
                                        <td colSpan="10">
                                            <table className="table table-sm">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Email</th>
                                                        <th>Phone</th>
                                                        <th>Payment Date</th>
                                                        <th>Address</th>
                                                        <th>Company</th>
                                                        <th>Amount</th>
                                                        <th>Status</th>
                                                        <th>Response</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr key={`billing-${order.id}`}>
                                                        <td>{order.billingDetails?.firstName} {order.billingDetails?.lastName}</td>
                                                        <td>{order.billingDetails?.email}</td>
                                                        <td>{order.billingDetails?.phone}</td>
                                                        <td>{moment(order.paymentDetails.date?.toDate()).format("lll")}</td>
                                                        <td>{order.billingDetails?.address1}
                                                            {order.billingDetails?.address2 && <br />}
                                                            {order.billingDetails?.address2}
                                                        </td>
                                                        <td>{order.billingDetails?.company}</td>
                                                        <td>${order.paymentDetails.amount}</td>
                                                        <td><span className={`badge ${order.paymentDetails.status === "completed" ? "bg-success" : "bg-danger"}`}>{order.paymentDetails.status}</span></td>
                                                        <td>{order.paymentDetails.message}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr> :
                                    <tr>
                                        <td>No Billing Details Found</td>
                                    </tr>
                                }
                                </>
                            )}
                        </React.Fragment>
                    )}
                    <tr ref={ref}>
                        <td colSpan="8">
                            {isFetchingNextPage ? (
                                <div className="text-center py-2">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : hasNextPage ? (
                                <div className="text-center py-2 text-muted">
                                    Scroll for more...
                                </div>
                            ) : filteredOrders.length > 0 ? (
                                <div className="text-center py-2 text-muted">
                                    No more records to load
                                </div>
                            ) : (
                                <div className="text-center py-2 text-muted">
                                    No records found
                                </div>
                            )}
                        </td>
                    </tr>
                </tbody>
            </AdminTable>
        </section>
    )
}

export default OrdersTable